import { useCreateAverageTicket } from 'pages/admin/demand/averageTicket/hooks/useCreateAverageTicket';
import { useEditAverageTicket } from 'pages/admin/demand/averageTicket/hooks/useEditAverageTicket';
import { averageTicketMessages } from 'pages/admin/demand/averageTicket/variables/averageTicketMessages';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSalesChannelList } from 'services/queryClient/wrapperHooks/salesChannel/useSalesChannelList';
import { renderFamily, renderProduct } from 'utils/forms/autocomplete';
import { currencyToNumber } from 'utils/functions/currencyToNumber';

import { makeGenericFormInputStructure } from '../../utils/forms/defaultInputs';
import {
	defaultFormStyle,
	defaultInputStyle,
} from '../../utils/forms/defaultsProps';
import { formFlexProps, rowFlexProps } from '../../utils/forms/defaultsProps';
import { validateRequired } from '../../utils/forms/validations';
import { LoadingSpinner } from '../loading/Loading';
import GenericForm, { IFormRows } from './GenericForm';

interface IRegisterAverageTicketForm {
	initialState: any;
	averageTicketId?: number;
	autocompleteAdditionalData?: any;
}

const RegisterAverageTicketForm = ({
	initialState,
	averageTicketId = 0,
	autocompleteAdditionalData,
}: IRegisterAverageTicketForm) => {
	const history = useHistory();

	const isEditing = !!averageTicketId;

	const { data: SalesChannelData, isLoading: isLoadingSalesChannelData } =
		useSalesChannelList();

	const [formInitialState, setFormInitialState] = useState<any>();

	const [familyId, setFamilyId] = useState<number>();
	const [payload, setPayload] = useState<any>();

	const { mutate: createAverageTicket } = useCreateAverageTicket();
	const { mutate: updateAverageTicket } =
		useEditAverageTicket(averageTicketId);

	useEffect(() => {
		setFamilyId(initialState?.familia_id);
	}, [initialState]);

	useEffect(() => {
		setFormInitialState(initialState);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialState]);

	const goToOpportunities = useCallback(() => {
		history.goBack();
	}, [history]);

	const salesChannelOptions = useMemo(() => {
		return (
			SalesChannelData?.map(channel => {
				return (
					<option value={channel?.id}>
						{channel?.nome_canal_venda}
					</option>
				);
			}) || []
		);
	}, [SalesChannelData]);

	const handleOnChangeFamily = useCallback(value => {
		setFamilyId(value?.id);
	}, []);

	const isDisabledProductAutocomplete = useCallback(() => {
		return !familyId;
	}, [familyId]);

	const shouldClearAutocompleteProduct = useCallback(() => {
		if (!familyId) return false;

		return familyId !== autocompleteAdditionalData?.product?.familia?.id;
	}, [familyId, autocompleteAdditionalData?.product?.familia?.id]);

	const onSubmit = useCallback(
		values => {
			const body = {
				produto_id: values?.produto_id,
				canal_venda_id: Number(values?.canal_venda_id),
				familia_id: values?.familia_id,
				recalcular: false,
				ticket_medio_editado: currencyToNumber(
					values?.ticket_medio_editado
				),
			};

			setPayload(body);

			if (isEditing) updateAverageTicket(body);
			else createAverageTicket(body);
		},
		[createAverageTicket, isEditing, updateAverageTicket]
	);

	const renderRows = useCallback((): IFormRows[] => {
		return [
			{
				type: 'common',
				rowFlexProps,
				columns: [
					{
						...makeGenericFormInputStructure({
							type: 'autocomplete',
							name: 'familia_id',
							label: averageTicketMessages.LABELS.FAMILY,
							showRequiredOnLabel: true,
							validate: { ...validateRequired() },
							inputProps: {
								'data-test':
									'autocomplete-familia-cadastrar_editar-page-preco_medio',
								...defaultInputStyle,
								searchType: 'familia',
								renderInputLabel: renderFamily,
								autocomplete: 'off',
								initialLabel: autocompleteAdditionalData?.family
									? renderFamily(
											autocompleteAdditionalData?.family
									  )
									: '',
								onChange: handleOnChangeFamily,
							},
						}),
					},
				],
			},
			{
				type: 'common',
				rowFlexProps,
				columns: [
					{
						...makeGenericFormInputStructure({
							type: 'autocomplete',
							name: 'produto_id',
							label: averageTicketMessages.LABELS.PRODUCT,
							showRequiredOnLabel: true,
							validate: { ...validateRequired() },
							disabled: isDisabledProductAutocomplete,
							inputProps: {
								'data-test':
									'autocomplete-produto-cadastrar_editar-page-preco_medio',
								...defaultInputStyle,
								searchType: 'produto',
								renderInputLabel: renderProduct,
								autocomplete: 'off',
								initialLabel:
									autocompleteAdditionalData?.product
										? renderProduct(
												autocompleteAdditionalData?.product
										  )
										: '',
								extraRequestData: {
									familia_id: familyId,
								},
								clearInput: shouldClearAutocompleteProduct,
							},
						}),
					},
				],
			},
			{
				type: 'common',
				rowFlexProps,
				columns: [
					{
						...makeGenericFormInputStructure({
							type: 'select',
							name: 'canal_venda_id',
							label: averageTicketMessages.LABELS.SALES_CHANNEL,
							showRequiredOnLabel: true,
							validate: { ...validateRequired() },
							inputProps: {
								'data-test':
									'select-canal_de_venda-cadastrar_editar-page-preco_medio',
								...defaultInputStyle,
								selectOptions: salesChannelOptions,
							},
						}),
					},
				],
			},
			{
				type: 'common',
				rowFlexProps,
				columns: [
					{
						...makeGenericFormInputStructure({
							type: 'decimal',
							name: 'ticket_medio_editado',
							label: averageTicketMessages.LABELS.AVERAGE_TICKET,
							showRequiredOnLabel: true,
							validate: { ...validateRequired() },
							inputProps: {
								'data-test':
									'input-preco_medio-cadastrar_editar-page-preco_medio',
								...defaultInputStyle,
								allowNegativeValue: true,
							},
						}),
					},
				],
			},
		];
	}, [
		autocompleteAdditionalData,
		familyId,
		handleOnChangeFamily,
		isDisabledProductAutocomplete,
		salesChannelOptions,
		shouldClearAutocompleteProduct,
	]);

	const renderForm = useCallback(() => {
		if (isLoadingSalesChannelData) return <LoadingSpinner />;

		return (
			<>
				<GenericForm
					dataTestProps={{
						cancel: 'button-cancelar-page-preco_medio',
						save: 'button-salvar-page-preco_medio',
					}}
					initialState={formInitialState}
					formFlexProps={formFlexProps}
					rows={renderRows()}
					style={defaultFormStyle}
					onSubmit={onSubmit}
					onCancel={goToOpportunities}
				/>
			</>
		);
	}, [
		formInitialState,
		renderRows,
		onSubmit,
		goToOpportunities,
		isLoadingSalesChannelData,
	]);

	return renderForm();
};

export default RegisterAverageTicketForm;
